import React, { useState, useRef, useEffect } from "react";

import JSZip from "jszip";

import FileUploadIcon from "@mui/icons-material/FileUpload";
import HomeIcon from "@mui/icons-material/Home";
import LayersIcon from "@mui/icons-material/Layers";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import MapIcon from "@mui/icons-material/Map";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SpeedIcon from "@mui/icons-material/Speed";

import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Style from "ol/style/Style";
import { createEmpty, extend } from "ol/extent";

import MapFullScreen from "../map/mapFullScreen";
import MapInfoCard from "../map/mapInfoCard";
import MapTileDropdown from "../map/mapTileDropdown";

import { MapSettingsDropdown } from "../map/mapSettingsDropdown";
import { MapLayersDropdown } from "../map/mapLayersDropdown";

import {
    createZeroConflictionLineFeature,
    getAsdFeatureStyleFromLayersAndSettings,
    getCoordinateLocationFromSettings,
    GetFeature,
    GetFeatures,
    getGeometryStyleFromFeatureAndSettings,
    getHistLineCoordinatesFromTrack,
    GetLaancAirspaceStyle,
    getLabelTextFromFeatureAndUser,
    GetLayer,
    getPredLineCoordinatesFromTrackAndSettings,
    getZeroConflictionLineCoordinates,
    getZeroConflictionLineStyleFromZeroConflictMessage
} from "../map/mapUtil";

import {
    getRoundedUpMsFromIsoString,
    getPlaybackFileValid,
    getPlaybackTimeValueLabelFromMs,
    getAsdFeaturesFromPlaybackFileAndTimestamp,
    getFlightVolumesFromPlaybackFileAndTimestamp,
    getReplaySpeedLabelFromMs
} from "./mapPlaybackUtil";

import { getFeaturesFromHimsSensors } from "../hms/hmsUtil";

import useAirspaceLayer from "../hooks/useAirspaceLayer";
import useOpenLayersGeocoder from "../hooks/useGeocoder";
import useOpenLayersMap from "../hooks/useOpenLayersMap";

import { useUserAuth } from "../contexts/authContext";
import { useMap } from "../contexts/mapContext";

const MapPlaybackFileViewer = ({ viewPlaybackFileData, setViewPlaybackFileData, playbackTime, setPlaybackTime, showToolbar = true }) => {
    const MAP_ID = "playback-map";

    const map = useOpenLayersMap(MAP_ID);

    const fileInputRef = useRef(null);
    const mapWrapperRef = useRef(null);
    const intervalRef = useRef(null);
    const mapLocationRef = useRef(null);
    const hoveredAsdFeatures = useRef([]);

    const asdSourceRef = useRef(null);
    const flightSourceRef = useRef(null);
    const constraintSourceRef = useRef(null);
    const alertVolumeSourceRef = useRef(null);
    const sensorSourceRef = useRef(null);

    const settingsRef = useRef(null);
    const layersRef = useRef(null);

    const { userMapSettings, user, setSnackbar, userProfileSettings, updateProfileSettings } = useUserAuth();
    const { sensors } = useMap();

    const [userMapLayers, setUserMapLayers] = useState(userProfileSettings.layers);
    const [mapContainer, setMapContainer] = useState(null);

    const [mapLayersOpen, setMapLayersOpen] = useState(false);
    const [mapLayersAnchorEl, setMapLayersAnchorEl] = useState(null);

    const [mapSettingsOpen, setMapSettingsOpen] = useState(false);
    const [mapSettingsAnchorEl, setMapSettingsAnchorEl] = useState(null);

    const [mapTileDropdownOpen, setMapTileDropdownOpen] = useState(false);
    const [mapTileDropdownAnchorEl, setMapTileDropdownAnchorEl] = useState(null);

    const [replaying, setReplaying] = useState(false);
    const [replaySpeedMenuAnchorEl, setReplaySpeedMenuAnchorEl] = useState(null);
    const [replaySpeedIndex, setReplaySpeedIndex] = useState(2);

    const replaySpeeds = [4000, 2000, 1000, 500, 250];

    useAirspaceLayer(map, setUserMapLayers);
    useOpenLayersGeocoder(map, showToolbar);

    // update features on map when the data and playback time change
    useEffect(() => {
        if (map && viewPlaybackFileData && playbackTime) {
            handleUpdateFeaturesOnMap(viewPlaybackFileData, playbackTime);
        } else if (map && viewPlaybackFileData) {
            const playbackDataStartTimeMs = new Date(viewPlaybackFileData.time_start_iso_str).getTime();
            setPlaybackTime(playbackDataStartTimeMs);
        }
    }, [map, viewPlaybackFileData, playbackTime]);

    // update hover behavior when settings change
    useEffect(() => {
        if (!map) return;

        let timeout;
        const handlePointerMove = ({ originalEvent, pixel }) => {
            const coordinates = map.getEventCoordinate(originalEvent);

            if (mapLocationRef.current) {
                mapLocationRef.current.innerHTML = getCoordinateLocationFromSettings(coordinates, userMapSettings);
            }
            if (userMapSettings.label_visible !== "off") {
                return;
            }
            hoveredAsdFeatures.current.forEach((feature) => {
                feature.set("show_label", false);

                const styleArr = feature.getStyle();
                if (Array.isArray(styleArr)) {
                    const id = feature.getId();
                    styleArr.forEach((style) => {
                        if (style.id === `${id}_label`) {
                            style.setText(undefined);
                        }
                    });
                }
            });
            hoveredAsdFeatures.current = [];

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                map.forEachFeatureAtPixel(pixel, (feature, layer) => {
                    if (!feature.get("asd") || layer !== GetLayer(map, "asd")) {
                        return;
                    }
                    const style = feature.getStyle();
                    if (Array.isArray(style) && style[0].getImage()) {
                        const newStyle = new Style({
                            text: getLabelTextFromFeatureAndUser(feature, user, userMapSettings)
                        });
                        newStyle.id = feature.getId() + "_label";
                        feature.setStyle([...style, newStyle]);

                        feature.set("show_label", true);
                        hoveredAsdFeatures.current.push(feature);
                    }
                });
            }, 250);
        };
        map.on("pointermove", handlePointerMove);

        return () => {
            clearTimeout(timeout);
            map.un("pointermove", handlePointerMove);
        };
    }, [map, userMapSettings]);

    // update profile settings when the layers change
    useEffect(() => {
        const updatedProfileSettings = {
            ...userProfileSettings,
            layers: userMapLayers
        };
        updateProfileSettings(updatedProfileSettings);
    }, [userMapLayers]);

    // render / update sensor features
    useEffect(() => {
        if (!map) return;

        const sensorFeatures = getFeaturesFromHimsSensors(sensors);
        if (!sensorSourceRef.current) {
            const source = new VectorSource({ features: sensorFeatures });
            sensorSourceRef.current = source;

            const visible = userMapLayers.radar_footprint;
            const layer = new VectorLayer({ source: source, properties: { name: "sensors", selectable: true }, visible: visible });
            map.addLayer(layer);
        } else {
            sensorSourceRef.current.clear();
            sensorSourceRef.current.addFeatures(sensorFeatures);
        }
    }, [map, sensors]);

    // update features when the map settings change
    useEffect(() => {
        // update base layer brightness
        const baseLayer = GetLayer(map, "base");
        baseLayer?.setOpacity(userMapSettings.brightness || 0);

        // update ASD Tracks
        const asdFeatures = GetFeatures(map, "asd");
        asdFeatures?.forEach((feature) => {
            const id = feature.getId();

            const isFeatureZcLine = id.includes("_") && !id.includes("_pl") && !id.includes("_hl") && !id.includes("_dpz");
            if (isFeatureZcLine) {
                const ownshipId = id.split("_")[0];
                const intruderId = id.split("_")[1];

                const ownship = GetFeature(map, "asd", ownshipId);
                const alerts = ownship.get("zero_confliction_alerts");

                const message = alerts.find((alert) => {
                    return alert.ownship_track.id === ownshipId && alert.intruder_track.id === intruderId;
                });
                const zeroConflictStyle = getZeroConflictionLineStyleFromZeroConflictMessage(message, ownship);
                feature.setStyle(zeroConflictStyle);
            } else {
                const updatedStyle = getAsdFeatureStyleFromLayersAndSettings(feature, userMapLayers, userMapSettings, user);
                feature.setStyle(updatedStyle);

                if (id.includes("_pl")) {
                    const message = feature.get("asd");
                    if (!message) return;

                    const coordinates = getPredLineCoordinatesFromTrackAndSettings(message, userMapSettings);
                    feature.getGeometry().setCoordinates(coordinates);
                }
                if (id.includes("_hl")) {
                    const message = feature.get("asd");
                    if (!message) return;

                    const coordinates = getHistLineCoordinatesFromTrack(message, userMapSettings);
                    feature.getGeometry().setCoordinates(coordinates);
                }
            }
        });
        // update CMP Volume opacity
        ["flights", "constraints", "alerts"].forEach((volume) => {
            const features = GetFeatures(map, volume);
            features?.forEach((feature) => {
                const style = getGeometryStyleFromFeatureAndSettings(feature, userMapSettings);
                feature.setStyle(style);
            });
        });
        settingsRef.current = userMapSettings;
        layersRef.current = userMapLayers;
    }, [map, userMapSettings, userMapLayers]);

    const handleFileUploadButtonClick = () => {
        if (replaying) {
            alert("Please pause or wait for your current replay to end before uploading a new file.");
        } else {
            fileInputRef.current.click();
        }
    };
    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = async (e) => {
                try {
                    const arrayBuffer = e.target.result;
                    let jsonContent;

                    if (file.name.endsWith(".zip") || file.type === "application/zip") {
                        const zip = await JSZip.loadAsync(arrayBuffer);

                        const jsonFile = Object.values(zip.files).find((f) => {
                            return f.name.endsWith(".json");
                        });
                        if (!jsonFile) {
                            return alert("No JSON file found inside the ZIP archive.");
                        }
                        const unzippedText = await jsonFile.async("string");
                        jsonContent = JSON.parse(unzippedText);
                    } else {
                        const decoder = new TextDecoder("utf-8");
                        const text = decoder.decode(arrayBuffer);

                        jsonContent = JSON.parse(text);
                    }
                    const isValidFormat = getPlaybackFileValid(jsonContent);
                    if (isValidFormat === "") {
                        setViewPlaybackFileData(jsonContent);
                    } else {
                        alert(isValidFormat);
                    }
                } catch (error) {
                    console.error("File processing error:", error);
                    alert("Could not process file. Ensure it is a valid JSON or ZIP file.");
                }
            };
            reader.onerror = (e) => {
                console.error("File reading error:", e.target.error);
            };
            reader.readAsArrayBuffer(file);
        } else {
            alert("Please upload a single file");
        }
    };
    const handleUpdateFeaturesOnMap = (playbackData, timeMs) => {
        // sort each event into its own data structure (to improve runtime)
        const asdMap = new Map();
        const flightMap = new Map();
        const constraintMap = new Map();
        const alertVolumeMap = new Map();
        const zeroConflictAlertMap = new Map();
        const alertVolumeAlertMap = new Map();
        const previousAsdLocationsMap = new Map();

        for (let i = 0; i < playbackData.events.length; i++) {
            const event = playbackData.events[i];
            const eventTimestampMs = new Date(event.timestamp_iso_str).getTime();
            if (eventTimestampMs > timeMs) break;

            if (event.type === "ASD_TRACK") {
                const position = [event.message.lon_deg, event.message.lat_deg];
                const previousLocations = previousAsdLocationsMap.get(event.message.id);

                if (Array.isArray(previousLocations) && previousLocations.length >= 99) {
                    const slicedPositions = [...previousLocations, position].slice(1);
                    previousAsdLocationsMap.set(event.message.id, slicedPositions);
                } else if (Array.isArray(previousLocations)) {
                    previousAsdLocationsMap.set(event.message.id, [...previousLocations, position]);
                } else {
                    previousAsdLocationsMap.set(event.message.id, [position]);
                }
                asdMap.set(event.message.id, event);
            } else if (event.type === "FLIGHT") {
                flightMap.set(event.message.flight_uuid, event);
            } else if (event.type === "CONSTRAINT") {
                constraintMap.set(event.message.constraint_uuid, event);
            } else if (event.type === "ALERT_VOLUME") {
                alertVolumeMap.set(event.message.uuid, event);
            } else if (event.type === "ZERO_CONFLICTION_ALERT") {
                zeroConflictAlertMap.set(event.message.alert_uuid, event);
            } else if (event.type === "ALERT_VOLUME_ALERT") {
                alertVolumeAlertMap.set(event.message.alert_id, event);
            }
        }
        let allFeatures = [];
        let isFirstRender = false;

        // add ASD tracks, Flights, Constraints, and Alert Volumes for the newly selected timestamp
        const mapLayers = [
            { layer: "ASD_TRACK", source: asdSourceRef, name: "asd" },
            { layer: "FLIGHT", source: flightSourceRef, name: "flights" },
            { layer: "CONSTRAINT", source: constraintSourceRef, name: "constraints" },
            { layer: "ALERT_VOLUME", source: alertVolumeSourceRef, name: "alerts" }
        ];
        mapLayers.forEach(({ name, layer, source }) => {
            if (!source || !source.current) {
                const newSource = new VectorSource({ features: [] });
                source.current = newSource;

                const newLayer = new VectorLayer({ source: newSource, zIndex: name === "asd" ? 3 : 2, properties: { name, selectable: true } });
                map.addLayer(newLayer);
                isFirstRender = true;
            }
            let newFeatures = [];
            if (layer === "ASD_TRACK") {
                newFeatures = getAsdFeaturesFromPlaybackFileAndTimestamp(
                    asdMap,
                    timeMs,
                    settingsRef.current,
                    user,
                    layersRef.current,
                    zeroConflictAlertMap,
                    alertVolumeAlertMap,
                    previousAsdLocationsMap
                );
            } else if (layer === "FLIGHT") {
                newFeatures = getFlightVolumesFromPlaybackFileAndTimestamp(flightMap, timeMs, settingsRef.current, layer);
            } else if (layer === "CONSTRAINT") {
                newFeatures = getFlightVolumesFromPlaybackFileAndTimestamp(constraintMap, timeMs, settingsRef.current, layer);
            } else if (layer === "ALERT_VOLUME") {
                newFeatures = getFlightVolumesFromPlaybackFileAndTimestamp(alertVolumeMap, timeMs, settingsRef.current, layer);
            }
            allFeatures = [...allFeatures, ...newFeatures];

            // add or update new features
            newFeatures.forEach((newFeature) => {
                const newFeatureId = newFeature.getId();
                const existingFeature = source.current.getFeatureById(newFeatureId);

                if (existingFeature) {
                    const newCoordinates = newFeature.getGeometry().getCoordinates();
                    existingFeature.getGeometry().setCoordinates(newCoordinates);

                    const newProperties = newFeature.getProperties();
                    existingFeature.setProperties(newProperties);

                    const newStyle = newFeature.getStyle();
                    existingFeature.setStyle(newStyle);
                } else {
                    source.current.addFeature(newFeature);
                }
            });

            // remove exired features
            const existingFeatures = source.current.getFeatures();
            existingFeatures.forEach((existingFeature) => {
                const featureExistsInNewTimeslot = newFeatures.find((newFeature) => {
                    return newFeature.getId() === existingFeature.getId();
                });
                if (featureExistsInNewTimeslot === undefined) {
                    source.current.removeFeature(existingFeature);
                }
            });
        });
        // create / update zero conflict lines if applicable
        zeroConflictAlertMap.forEach((event) => {
            const message = event.message;
            const zcLineID = `${message.ownship_track.id}_${message.intruder_track.id}`;

            if (message.state === "ACTIVE") {
                const ownshipFeature = GetFeature(map, "asd", message.ownship_track.id);
                const intruderFeature = GetFeature(map, "asd", message.intruder_track.id);

                if (!ownshipFeature || !intruderFeature) {
                    return;
                }
                const zcLineFeature = GetFeature(map, "asd", zcLineID);
                if (zcLineFeature) {
                    const coordinates = getZeroConflictionLineCoordinates(ownshipFeature, intruderFeature);
                    zcLineFeature.getGeometry().setCoordinates(coordinates);

                    const style = getZeroConflictionLineStyleFromZeroConflictMessage(message, ownshipFeature);
                    zcLineFeature.setStyle(style);
                } else {
                    const newZcLineFeature = createZeroConflictionLineFeature(ownshipFeature, intruderFeature, message);
                    asdSourceRef.current.addFeature(newZcLineFeature);
                }
            } else {
                const zcLineFeature = GetFeature(map, "asd", zcLineID);
                if (zcLineFeature) {
                    asdSourceRef.current.removeFeature(zcLineFeature);
                }
            }
        });
        if (isFirstRender) {
            handleZoomToFeatures(allFeatures);
        }
    };
    const handleHomeButtonClick = () => {
        if (map && map.getView()) {
            map.getView().setCenter([userMapSettings.longitude, userMapSettings.latitude]);
            map.getView().setZoom(12);
        }
    };
    const handleToggleLayersButtonClick = (e) => {
        setMapLayersAnchorEl(e.currentTarget);
        setMapLayersOpen((prev) => !prev);
    };
    const handleSettingsButtonClick = (e) => {
        setMapSettingsOpen((prev) => !prev);
        setMapSettingsAnchorEl(e.currentTarget);
    };
    const handleMapTileDropdownButtonClick = (e) => {
        setMapTileDropdownOpen((prev) => !prev);
        setMapTileDropdownAnchorEl(e.currentTarget);
    };
    const handleFullScreenButtonClick = () => {
        try {
            if (document.fullscreenElement) {
                document.exitFullscreen();
                return;
            } else if (!mapWrapperRef.current) {
                return;
            }
            mapWrapperRef.current.requestFullscreen();
            if (!mapContainer) {
                setMapContainer(mapWrapperRef.current);
            }
        } catch (err) {
            console.error(err);
        }
    };
    const handleMapLayersChange = (setting, value) => {
        const mapLayers = { ...userMapLayers };
        const toggleSettings = ["surveillance_sources", "operations", "constraints", "alert_volumes", "radar_footprint"];

        if (toggleSettings.includes(setting)) {
            const visibility = !mapLayers[setting];
            GetLayer(map, value)?.setVisible(visibility);
            mapLayers[setting] = visibility;
        } else if (setting === "surveillance_sources_list") {
            mapLayers[setting].push(value);
        } else if (setting === "airspaces") {
            const airspaces = mapLayers[setting];
            if (value) {
                const airspace = airspaces.find((airspace) => airspace.local_type === value);
                airspace.visible = !airspace.visible;
            } else {
                const visibility = !airspaces[0].visible;
                airspaces.forEach((airspace) => (airspace.visible = visibility));
            }
            GetLayer(map, "LAANC_AIRSPACE")?.setStyle((feature) => GetLaancAirspaceStyle(feature, airspaces));
        } else {
            mapLayers[setting] = value;
        }
        setUserMapLayers(mapLayers);
    };
    const handleNarTrackSettingChange = (e) => {
        const mapLayers = {
            ...userMapLayers,
            nar_tracks: e.target.checked
        };
        setUserMapLayers(mapLayers);
    };
    const handlePlayButtonClicked = () => {
        setReplaying(true);

        const selectedReplaySpeed = replaySpeeds[replaySpeedIndex];
        handleStartPlaybackTimer(selectedReplaySpeed);
    };
    const handlePauseButtonClicked = () => {
        setReplaying(false);
        handleClearPlaybackTimer();
    };
    const handleStartPlaybackTimer = (intervalMs) => {
        const startTimeIso = new Date(playbackTime).toISOString();
        const startTimeMs = getRoundedUpMsFromIsoString(startTimeIso);
        let i = 1;

        const interval = setInterval(() => {
            const newTimeValueMs = startTimeMs + 1000 * i;
            const maxTimeValueMs = getRoundedUpMsFromIsoString(viewPlaybackFileData.time_end_iso_str);

            if (newTimeValueMs > maxTimeValueMs) {
                handleClearPlaybackTimer();
                setReplaying(false);
            } else {
                setPlaybackTime(newTimeValueMs);
                i += 1;
            }
        }, intervalMs);

        intervalRef.current = interval;
    };
    const handleClearPlaybackTimer = () => {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
    };
    const handleZoomToFeatures = (features) => {
        if (!map || !features || !features.length) return;

        const extent = createEmpty();
        features.forEach((feature) => {
            extend(extent, feature.getGeometry().getExtent());
        });
        map.getView().fit(extent, {
            padding: [40, 40, 40, 40]
        });
    };
    const handleReplaySpeedChange = (index) => {
        setReplaySpeedMenuAnchorEl(null);
        setReplaySpeedIndex(index);

        if (intervalRef.current) {
            handleClearPlaybackTimer();

            const newReplaySpeedMs = replaySpeeds[index];
            handleStartPlaybackTimer(newReplaySpeedMs);
        }
    };
    return (
        <Box
            ref={mapWrapperRef}
            sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", position: "relative", height: "100%", width: "100%" }}>
                <MapFullScreen
                    map_id={MAP_ID}
                    map={map}
                    buttons={
                        showToolbar
                            ? [
                                  {
                                      id: "fileUpload",
                                      name: "Upload file",
                                      icon: <FileUploadIcon />,
                                      onClick: handleFileUploadButtonClick,
                                      visible: true
                                  },
                                  {
                                      id: "viewHome",
                                      name: "View Home",
                                      visible: true,
                                      icon: <HomeIcon />,
                                      onClick: handleHomeButtonClick
                                  },
                                  {
                                      id: "toggleLayers",
                                      name: "Toggle Layers",
                                      visible: true,
                                      icon: <LayersIcon />,
                                      onClick: handleToggleLayersButtonClick
                                  },
                                  {
                                      id: "editSettings",
                                      name: "Edit Settings",
                                      visible: true,
                                      icon: <SettingsIcon />,
                                      onClick: handleSettingsButtonClick
                                  },
                                  {
                                      id: "toggleMapTiles",
                                      name: "Map Tiles",
                                      visible: true,
                                      icon: <MapIcon />,
                                      onClick: handleMapTileDropdownButtonClick
                                  },
                                  {
                                      id: "toggleFullscreen",
                                      name: document.fullscreenElement ? "Exit Fullscreen" : "Enter Fullscreen",
                                      visible: true,
                                      icon: document.fullscreenElement ? <CloseIcon /> : <OpenInFullIcon />,
                                      onClick: handleFullScreenButtonClick
                                  },
                                  {
                                      id: "close",
                                      name: "Close",
                                      visible: document.fullscreenElement ? false : true,
                                      icon: <CloseIcon />,
                                      onClick: () => setViewPlaybackFileData(null)
                                  }
                              ]
                            : []
                    }
                >
                    <MapInfoCard map={map} airspaceSubLayers={userMapLayers.airspaces} mapContainer={mapContainer} />
                </MapFullScreen>

                {showToolbar ? (
                    <Typography
                        ref={mapLocationRef}
                        variant="caption"
                        sx={{ position: "absolute", right: "0.5em", bottom: ".5em", zIndex: 1, textShadow: "0 0 10px #000" }}
                    />
                ) : (
                    <></>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    onClick={(e) => (e.target.value = "")}
                    multiple
                    style={{ display: "none" }}
                    accept=".json, .zip, application/json, application/zip"
                />

                {viewPlaybackFileData ? (
                    <Paper
                        sx={{
                            position: "absolute",
                            left: "50%",
                            bottom: "8px",
                            transform: "translateX(-50%)",
                            width: "400px",
                            display: "flex",
                            maxWidth: "95%",
                            gap: 1,
                            px: 1
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                            {replaying ? (
                                <Tooltip title="Pause" placement="top">
                                    <IconButton size="small" onClick={handlePauseButtonClicked} data-testid="stopPlayback">
                                        <PauseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Play" placement="top">
                                    <IconButton size="small" onClick={handlePlayButtonClicked} data-testid="startPlayback">
                                        <PlayArrowIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={getReplaySpeedLabelFromMs(replaySpeeds[replaySpeedIndex])} placement="top">
                                <IconButton size="small" onClick={(e) => setReplaySpeedMenuAnchorEl(e.currentTarget)} data-testid="speedPlayback">
                                    <SpeedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Slider
                            valueLabelDisplay="on"
                            step={1000}
                            value={playbackTime}
                            onChange={(e) => setPlaybackTime(e.target.value)}
                            min={getRoundedUpMsFromIsoString(viewPlaybackFileData.time_start_iso_str)}
                            max={getRoundedUpMsFromIsoString(viewPlaybackFileData.time_end_iso_str)}
                            valueLabelFormat={getPlaybackTimeValueLabelFromMs(playbackTime)}
                            disabled={replaying}
                            size="small"
                            sx={{ mx: 1, flex: 1 }}
                        />
                    </Paper>
                ) : (
                    <></>
                )}
                {mapSettingsOpen ? (
                    <MapSettingsDropdown
                        mapSettingsOpen={mapSettingsOpen}
                        setMapSettingsOpen={setMapSettingsOpen}
                        mapSettingsAnchorEl={mapSettingsAnchorEl}
                        setSnackbar={setSnackbar}
                        mapContainer={mapContainer}
                    />
                ) : (
                    <></>
                )}
                {mapTileDropdownOpen ? (
                    <MapTileDropdown
                        mapTileDropdownOpen={mapTileDropdownOpen}
                        setMapTileDropdownOpen={setMapTileDropdownOpen}
                        mapTileDropdownAnchorEl={mapTileDropdownAnchorEl}
                        mapContainer={mapContainer}
                    />
                ) : (
                    <></>
                )}
                <MapLayersDropdown
                    mapLayersOpen={mapLayersOpen}
                    setMapLayersOpen={setMapLayersOpen}
                    mapLayersAnchorEl={mapLayersAnchorEl}
                    altitudeLayerRange={userMapLayers.altitude_range}
                    handleAltitudeLayerRangeChanged={(e) => handleMapLayersChange("altitude_range", e.target.value)}
                    narChecked={userMapLayers.nar_tracks}
                    handleNarTrackSettingChange={handleNarTrackSettingChange}
                    sections={[
                        {
                            title: "General",
                            layers: [
                                {
                                    label: "Surveillance Sources",
                                    checked: userMapLayers.surveillance_sources,
                                    handleLayerClick: () => handleMapLayersChange("surveillance_sources", "asd"),
                                    sublayers: userMapLayers.surveillance_sources_list
                                }
                            ]
                        },
                        {
                            title: "Volumes",
                            layers: [
                                {
                                    label: "Airspaces",
                                    checked: userMapLayers.airspaces.every(({ visible }) => visible),
                                    indeterminate: !userMapLayers.airspaces.every(({ visible }) => visible === userMapLayers.airspaces[0].visible),
                                    handleLayerClick: () => handleMapLayersChange("airspaces"),
                                    sublayers: userMapLayers.airspaces.map((airspace) => {
                                        return {
                                            label: airspace.local_type,
                                            checked: airspace.visible,
                                            handleSublayerClick: () => handleMapLayersChange("airspaces", airspace.local_type)
                                        };
                                    })
                                },
                                {
                                    label: "Operations",
                                    checked: userMapLayers.operations,
                                    handleLayerClick: () => handleMapLayersChange("operations", "flights"),
                                    sublayers: []
                                },
                                {
                                    label: "Constraints",
                                    checked: userMapLayers.constraints,
                                    handleLayerClick: () => handleMapLayersChange("constraints", "constraints"),
                                    sublayers: []
                                },
                                {
                                    label: "Alert Volumes",
                                    checked: userMapLayers.alert_volumes,
                                    handleLayerClick: () => handleMapLayersChange("alert_volumes", "alerts"),
                                    sublayers: []
                                },
                                {
                                    label: "Radar Footprint",
                                    checked: userMapLayers.radar_footprint,
                                    handleLayerClick: () => handleMapLayersChange("radar_footprint", "sensors"),
                                    sublayers: []
                                }
                            ]
                        }
                    ]}
                    mapContainer={mapContainer}
                />
                <Menu
                    anchorEl={replaySpeedMenuAnchorEl}
                    open={Boolean(replaySpeedMenuAnchorEl)}
                    onClose={() => setReplaySpeedMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    {replaySpeeds.map((speed, index) => (
                        <MenuItem
                            key={index}
                            selected={replaySpeedIndex === index}
                            onClick={() => handleReplaySpeedChange(index)}
                            dense
                            sx={{ justifyContent: "center" }}
                        >
                            {getReplaySpeedLabelFromMs(speed)}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        </Box>
    );
};

export default MapPlaybackFileViewer;
